import React, { Key, ReactNode, useEffect } from "react";

import FormCardContainer from "@impero-com/common/components/formCardContainer";
import { NoAccessCard } from "@impero-com/common/components/noAccessCard";
import { LoggedInHTTPError } from "@impero-com/common/errorPages/loggedInHTTPError";
import { t } from "@impero-com/common/utils/i18n";
import { useOrgCookie } from "@impero-com/common/utils/orgCookie";
import { Button, CardFooter, FormSubtitle, FormTitle } from "@impero-com/ui";
import * as Sentry from "@sentry/react";
import { AxiosError } from "axios";

import { UserProfile } from "api/generated/model";
import { useOrgUserProfileCookie } from "utils/profileCookie";

const isAxiosError = (error: Error | AxiosError): error is AxiosError => {
    return (error as AxiosError).isAxiosError === true;
};
const RuntimeError = ({ inline }: { inline?: boolean }) => {
    return (
        <FormCardContainer fullWidth={true} inline={inline}>
            <FormTitle title={t("ErrorPage_RuntimeError")} />
            <FormSubtitle subtitle={t("ErrorPage_RuntimeErrorText")} />
            <CardFooter>
                <Button
                    id="client-error-button"
                    variant="link"
                    onClick={() => window.location.reload()}
                >
                    {t("ErrorPage_Reload")}
                </Button>
            </CardFooter>
        </FormCardContainer>
    );
};

interface ErrorBoundaryClassProps {
    inline?: boolean;
    children: ReactNode;
    key: Key;
    orgProfileCookie?: UserProfile | null;
}
interface ErrorBoundaryClassState {
    hasError: boolean;
    httpErrorCode: number | null;
}
class ErrorBoundaryClass extends React.Component<
    ErrorBoundaryClassProps,
    ErrorBoundaryClassState
> {
    state = {
        hasError: false,
        httpErrorCode: null,
    };
    static getDerivedStateFromError(error: Error | AxiosError) {
        if (isAxiosError(error)) {
            return {
                hasError: true,
                httpErrorCode: error.response?.status ?? null,
            };
        } else {
            Sentry.captureException(error);
            return { hasError: true };
        }
    }
    componentDidCatch(error: Error): void {
        Sentry.captureException(error);
    }
    render() {
        if (!this.state.hasError) {
            return this.props.children;
        }

        if (
            this.props.orgProfileCookie !== null &&
            this.state.httpErrorCode === 403
        ) {
            return <NoAccessCard inline={this.props.inline} />;
        }

        if (this.state.httpErrorCode) {
            return (
                <LoggedInHTTPError
                    code={this.state.httpErrorCode}
                    inline={this.props.inline}
                />
            );
        }

        return <RuntimeError inline={this.props.inline} />;
    }
}

interface ErrorBoundaryProps {
    inline?: boolean;
    children: React.ReactNode;
    routeKey: Key;
}
const ErrorBoundary = ({ children, routeKey, inline }: ErrorBoundaryProps) => {
    const orgProfileCookie = useOrgUserProfileCookie();
    const orgCookie = useOrgCookie();
    useEffect(() => {
        Sentry.setTags({
            shortName: orgCookie?.shortName,
            culture: orgProfileCookie?.culture,
        });
        Sentry.setContext("metadata", {
            userId: orgProfileCookie?.userId,
        });
    }, [orgCookie, orgProfileCookie]);

    return (
        <ErrorBoundaryClass
            key={routeKey}
            orgProfileCookie={orgProfileCookie}
            inline={inline}
        >
            {children}
        </ErrorBoundaryClass>
    );
};

export { ErrorBoundary };
